import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ArrowSquareOut, ChartLineUp, Desktop, Dress, Gear, Headset, House, Layout, List, MegaphoneSimple, Moon, Receipt, SealPercent, SignOut, Sun, Truck, UserCircle, Users, Warehouse,
} from '@phosphor-icons/react';
import type { Permission } from '@aramis/shared';
import { formatToman, toFaDigits } from '@aramis/shared';
import { accessToken, api, ensureSession } from '../lib/api';
import { useAuth, useTheme, type ThemeChoice } from '../lib/auth';
import { IconButton, cx } from '../ui/core';
import { Menu } from '../ui/overlay';
import { AramisMark } from './Brand';

interface NavItem { to: string; label: string; icon: ReactNode; perm?: Permission; badge?: number }

function useLiveEvents() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    let es: EventSource | null = null, stopped = false, retry: ReturnType<typeof setTimeout>;
    const connect = async () => {
      if (stopped || !(await ensureSession())) return;
      es = new EventSource(`/v1/realtime/admin?token=${encodeURIComponent(accessToken() ?? '')}`);
      es.addEventListener('order.paid', (e) => {
        const d = JSON.parse((e as MessageEvent).data);
        toast.success(`سفارش ${toFaDigits(d.number)} پرداخت شد`, { description: formatToman(d.total), action: { label: 'دیدن', onClick: () => navigate(`/orders/${d.id}`) } });
        void qc.invalidateQueries();
      });
      // everything else: refetch whatever is on screen (unchanged data keeps its identity, so open forms are not reset)
      for (const ev of ['order.updated', 'order.placed', 'stock.out', 'changed', 'customer.created', 'ticket.message']) es.addEventListener(ev, () => { void qc.invalidateQueries(); });
      es.addEventListener('return.created', (e) => { const d = JSON.parse((e as MessageEvent).data); toast(`درخواست مرجوعی برای سفارش ${toFaDigits(d.orderNumber)}`); void qc.invalidateQueries(); });
      es.addEventListener('ticket.created', (e) => { const d = JSON.parse((e as MessageEvent).data); toast(`پیام پشتیبانی جدید: ${d.subject}`); void qc.invalidateQueries(); });
      // access tokens are short-lived: reconnect with a fresh one when the stream drops
      es.onerror = () => { es?.close(); retry = setTimeout(connect, 5000); };
    };
    void connect();
    return () => { stopped = true; es?.close(); clearTimeout(retry); };
  }, [qc, navigate]);
}

export function Shell() {
  const { me, can, logout } = useAuth();
  const [theme, setTheme] = useTheme();
  const [mobileNav, setMobileNav] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileNav(false), [location.pathname]);
  useLiveEvents();

  const counts = useQuery({
    queryKey: ['nav-counts'],
    queryFn: async () => {
      const [orders, support] = await Promise.all([
        can('orders.view') ? api('/admin/orders', { query: { pageSize: 1 } }) : null,
        can('support.manage') ? api('/admin/support', { query: { pageSize: 1 } }) : null,
      ]);
      return { toConfirm: orders?.tabs?.awaiting_confirmation ?? 0, openTickets: support?.counts?.open ?? 0 };
    },
    refetchInterval: 60_000,
  });

  const nav = ([
    { to: '/', label: 'داشبورد', icon: <House size={20} />, perm: 'dashboard.view' },
    { to: '/orders', label: 'سفارش‌ها', icon: <Receipt size={20} />, perm: 'orders.view', badge: counts.data?.toConfirm },
    { to: '/products', label: 'محصولات', icon: <Dress size={20} />, perm: 'products.view' },
    { to: '/inventory', label: 'انبار', icon: <Warehouse size={20} />, perm: 'products.view' },
    { to: '/customers', label: 'مشتریان', icon: <Users size={20} />, perm: 'customers.view' },
    { to: '/promotions', label: 'تخفیف‌ها', icon: <SealPercent size={20} />, perm: 'promotions.manage' },
    { to: '/notifications', label: 'اعلان‌ها', icon: <MegaphoneSimple size={20} />, perm: 'notifications.send' },
    { to: '/content', label: 'ویترین فروشگاه', icon: <Layout size={20} />, perm: 'content.manage' },
    { to: '/shipping', label: 'ارسال', icon: <Truck size={20} />, perm: 'shipping.manage' },
    { to: '/support', label: 'پشتیبانی', icon: <Headset size={20} />, perm: 'support.manage', badge: counts.data?.openTickets },
    { to: '/reports', label: 'گزارش‌ها', icon: <ChartLineUp size={20} />, perm: 'reports.view' },
    { to: '/settings', label: 'تنظیمات', icon: <Gear size={20} /> },
  ] satisfies NavItem[]).filter((n) => !n.perm || can(n.perm));

  const themeIcon = theme === 'dark' ? <Moon size={18} /> : theme === 'light' ? <Sun size={18} /> : <Desktop size={18} />;
  const sidebar = (
    <nav aria-label="بخش‌های مدیریت" className="flex h-full flex-col gap-0.5 p-3">
      <div className="mb-4 flex h-12 items-center gap-2.5 px-2">
        <AramisMark className="size-7 text-text" />
        <div className="leading-tight"><div className="text-[15px] font-bold">آرامیس</div><div className="text-[11px] text-muted">پنل مدیریت فروشگاه</div></div>
      </div>
      {nav.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === '/'}
          className={({ isActive }) => cx('flex h-10 items-center gap-3 rounded-control px-2.5 text-sm transition-colors', isActive ? 'bg-surface font-medium text-text shadow-[0_0_0_1px_var(--m-line)]' : 'text-muted hover:bg-surface/60 hover:text-text')}>
          {({ isActive }) => (<>
            <span className={isActive ? 'text-accent' : ''}>{n.icon}</span>
            <span className="flex-1">{n.label}</span>
            {!!n.badge && <span className="num rounded-control bg-accent px-1.5 text-[11px] leading-5 text-on-accent">{toFaDigits(n.badge)}</span>}
          </>)}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[232px_minmax(0,1fr)]">
      <aside className="sticky top-0 hidden h-dvh overflow-y-auto border-e border-line bg-canvas lg:block">{sidebar}</aside>
      <Dialog.Root open={mobileNav} onOpenChange={setMobileNav}>
        <Dialog.Portal>
          <Dialog.Overlay className="overlay fixed inset-0 z-50 bg-[var(--m-scrim)] lg:hidden" />
          <Dialog.Content className="fixed inset-y-0 right-0 z-[60] w-[260px] overflow-y-auto bg-canvas shadow-xl lg:hidden" aria-describedby={undefined}>
            <Dialog.Title className="sr-only">منو</Dialog.Title>
            {sidebar}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-canvas/90 px-4 backdrop-blur-sm sm:px-6">
          <IconButton label="منو" className="lg:hidden" onClick={() => setMobileNav(true)}><List size={20} /></IconButton>
          <div className="flex-1" />
          <a href={import.meta.env.VITE_STORE_URL ?? 'http://127.0.0.1:3000'} target="_blank" rel="noreferrer" className="hidden h-9 items-center gap-1.5 rounded-control px-3 text-[13px] text-muted hover:bg-sunken hover:text-text sm:inline-flex">
            <ArrowSquareOut size={16} /> دیدن فروشگاه
          </a>
          <Menu trigger={<IconButton label="پوسته">{themeIcon}</IconButton>} items={(['system', 'light', 'dark'] as ThemeChoice[]).map((t) => ({
            label: { system: 'مطابق دستگاه', light: 'روشن', dark: 'تیره' }[t], icon: { system: <Desktop size={16} />, light: <Sun size={16} />, dark: <Moon size={16} /> }[t], onSelect: () => setTheme(t),
          }))} />
          <Menu trigger={<button className="flex h-9 items-center gap-2 rounded-control px-2 text-sm hover:bg-sunken"><UserCircle size={22} className="text-muted" /><span className="hidden sm:inline">{me?.name}</span></button>}
            items={[{ label: <span className="text-muted">{me?.role.name}</span>, onSelect: () => undefined, disabled: true }, 'sep', { label: 'خروج از حساب', icon: <SignOut size={16} />, onSelect: () => void logout(), danger: true }]} />
        </header>
        <main className="mx-auto w-full max-w-[1320px] px-4 py-6 sm:px-6 lg:px-8"><Outlet /></main>
      </div>
    </div>
  );
}
